import { useState, useEffect } from 'react';
import { Copy, Check, Pencil, Send, ChevronDown, ChevronUp, ExternalLink, Loader2, Bookmark, ImagePlus, RefreshCw, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { coworkApi, API_BASE_URL, authHeaders } from '../api/coworkApi';

export interface ContentCardData {
  contentId: string;
  platform: string;
  title: string;
  body: string;
  hashtags: string[];
  visual_direction?: string;
  post_type: string;
}

const PLATFORM_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  linkedin: { label: 'LinkedIn', color: '#0a66c2', bg: '#0a66c215' },
  instagram: { label: 'Instagram', color: '#e1306c', bg: '#e1306c15' },
  twitter: { label: 'X / Twitter', color: '#1d9bf0', bg: '#1d9bf015' },
  tiktok: { label: 'TikTok', color: '#010101', bg: '#01010110' },
  facebook: { label: 'Facebook', color: '#1877f2', bg: '#1877f215' },
  generic: { label: 'Content', color: '#667085', bg: '#66708515' },
};

export function ContentCard({ card, onSave }: { card: ContentCardData; onSave?: () => void }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(card.body);
  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [saved, setSaved] = useState(false);
  const [savedToQueue, setSavedToQueue] = useState(false);
  const [hasLinkedIn, setHasLinkedIn] = useState(false);
  const [visuals, setVisuals] = useState<string[]>([]);
  const [visualIndex, setVisualIndex] = useState(0);
  const [generatingVisual, setGeneratingVisual] = useState(false);

  // Check if user has a connected LinkedIn account
  useEffect(() => {
    coworkApi.integrations.list().then(({ integrations }) => {
      setHasLinkedIn(integrations.some(i => i.provider === 'linkedin'));
    }).catch(() => {});
  }, []);

  const platform = PLATFORM_CONFIG[card.platform] || PLATFORM_CONFIG.generic;
  const lines = card.body.split('\n');
  const isLong = lines.length > 6;
  const displayText = expanded || !isLong ? card.body : lines.slice(0, 6).join('\n') + '…';

  const handleCopy = () => {
    navigator.clipboard.writeText(card.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveEdit = async () => {
    try {
      await fetch(`${API_BASE_URL}/v1/content-queue/${card.contentId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ editedBody: editText }),
      });
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // silently fail — user can retry
    }
  };

  const handleGenerateVisual = async () => {
    setGeneratingVisual(true);
    try {
      const result = await coworkApi.contentQueue.generateVisual(card.contentId, card.visual_direction);
      if (result.imageUrl) {
        setVisuals(prev => [...prev, result.imageUrl!]);
        setVisualIndex(visuals.length); // point to the new one
      }
    } catch {
      // silently fail
    } finally {
      setGeneratingVisual(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-50">
        <span
          className="text-[10px] px-2 py-0.5 rounded-full"
          style={{ background: platform.bg, color: platform.color, fontWeight: 600 }}
        >
          {platform.label}
        </span>
        <span className="text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>
          {card.title}
        </span>
        {card.post_type !== 'text' && (
          <span className="text-[10px] text-[#98a2b3] bg-gray-50 px-1.5 py-0.5 rounded">
            {card.post_type}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3">
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              className="w-full text-[12.5px] text-[#344054] leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-200 outline-none focus:border-gray-300 resize-none"
              rows={Math.min(editText.split('\n').length + 2, 15)}
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                className="text-[11px] px-3 py-1.5 rounded-lg bg-[#101828] text-white hover:bg-[#1d2939] transition-colors"
                style={{ fontWeight: 500 }}
              >
                Save changes
              </button>
              <button
                onClick={() => { setEditing(false); setEditText(card.body); }}
                className="text-[11px] px-3 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-colors"
                style={{ fontWeight: 500 }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[12.5px] text-[#344054] leading-relaxed whitespace-pre-line">
              {displayText}
            </p>
            {isLong && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-[11px] text-[#98a2b3] hover:text-[#667085] mt-1.5 transition-colors"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>

      {/* Hashtags */}
      {card.hashtags.length > 0 && !editing && (
        <div className="flex flex-wrap gap-1 px-4 pb-2">
          {card.hashtags.map((tag, i) => (
            <span key={i} className="text-[10px] text-[#0a66c2] bg-[#0a66c208] px-1.5 py-0.5 rounded">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Visual direction */}
      {card.visual_direction && !editing && visuals.length === 0 && (
        <div className="px-4 pb-2">
          <p className="text-[10px] text-[#98a2b3] italic">
            Visual: {card.visual_direction}
          </p>
        </div>
      )}

      {/* Generated visuals */}
      {visuals.length > 0 && !editing && (
        <div className="px-4 pb-3">
          <div className="relative rounded-lg overflow-hidden bg-gray-50">
            <img
              src={`${API_BASE_URL}${visuals[visualIndex]}`}
              alt="Generated visual"
              className="w-full object-cover"
              style={{ maxHeight: 320 }}
            />
            {/* Navigation arrows */}
            {visuals.length > 1 && (
              <>
                <button
                  onClick={() => setVisualIndex(i => (i - 1 + visuals.length) % visuals.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setVisualIndex(i => (i + 1) % visuals.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
            {/* Counter + download */}
            <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
              {visuals.length > 1 && (
                <span className="text-[10px] text-white bg-black/50 px-2 py-0.5 rounded-full">
                  {visualIndex + 1} / {visuals.length}
                </span>
              )}
              <button
                onClick={async () => {
                  try {
                    const r = await fetch(`${API_BASE_URL}${visuals[visualIndex]}`);
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `${card.title.replace(/[^a-zA-Z0-9]/g, '-')}-visual.png`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch { window.open(`${API_BASE_URL}${visuals[visualIndex]}`, '_blank'); }
                }}
                className="w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generating indicator */}
      {generatingVisual && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 text-[11px] text-[#667085] bg-gray-50 rounded-lg px-3 py-2.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Generating visual{visuals.length > 0 ? ' (new variation)' : ''}…
          </div>
        </div>
      )}

      {/* Actions */}
      {!editing && (
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-t border-gray-50">
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-all"
            style={{ fontWeight: 500 }}
          >
            {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-all"
            style={{ fontWeight: 500 }}
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
          <button
            onClick={handleGenerateVisual}
            disabled={generatingVisual}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-all disabled:opacity-50"
            style={{ fontWeight: 500 }}
          >
            {visuals.length > 0 ? (
              <><RefreshCw className={`w-3 h-3 ${generatingVisual ? 'animate-spin' : ''}`} /> Regenerate</>
            ) : (
              <><ImagePlus className={`w-3 h-3 ${generatingVisual ? 'animate-pulse' : ''}`} /> Generate Visual</>
            )}
          </button>
          {savedToQueue ? (
            <span className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-green-600 bg-green-50 transition-all" style={{ fontWeight: 500 }}>
              <Check className="w-3 h-3" />
              Saved!
            </span>
          ) : (
            <button
              onClick={async () => {
                try {
                  await fetch(`${API_BASE_URL}/v1/content-queue/${card.contentId}`, {
                    method: 'PATCH',
                    headers: authHeaders(),
                    body: JSON.stringify({ status: 'saved' }),
                  });
                  setSavedToQueue(true);
                  onSave?.();
                } catch { /* ignore */ }
              }}
              className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-all"
              style={{ fontWeight: 500 }}
            >
              <Bookmark className="w-3 h-3" />
              Save
            </button>
          )}
          {saved && (
            <span className="text-[11px] text-green-600 ml-1" style={{ fontWeight: 500 }}>
              Edited!
            </span>
          )}
          <div className="flex-1" />
          {published ? (
            <span className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg text-green-600 bg-green-50" style={{ fontWeight: 500 }}>
              <Check className="w-3 h-3" />
              Published
            </span>
          ) : publishing ? (
            <span className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg text-[#667085]" style={{ fontWeight: 500 }}>
              <Loader2 className="w-3 h-3 animate-spin" />
              Publishing…
            </span>
          ) : (
            <div className="flex items-center gap-1.5">
              {publishError && (
                <span className="text-[10px] text-red-500 mr-1">{publishError}</span>
              )}
              {/* Direct publish via API (when LinkedIn connected) */}
              {hasLinkedIn && card.platform === 'linkedin' ? (
                <button
                  onClick={async () => {
                    setPublishing(true);
                    setPublishError('');
                    try {
                      const result = await coworkApi.contentQueue.publish(card.contentId);
                      if (result.error) {
                        setPublishError(result.error);
                      } else {
                        setPublished(true);
                      }
                    } catch (err) {
                      setPublishError('Publish failed');
                    } finally {
                      setPublishing(false);
                    }
                  }}
                  className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg text-white hover:opacity-90 transition-all"
                  style={{ fontWeight: 500, background: '#0a66c2' }}
                >
                  <Send className="w-3 h-3" />
                  Publish to LinkedIn
                </button>
              ) : (
                /* Share URL fallback for all platforms */
                <button
                  onClick={() => {
                    const text = encodeURIComponent(
                      (editText || card.body) +
                      (card.hashtags.length > 0 ? '\n\n' + card.hashtags.map(h => `#${h}`).join(' ') : '')
                    );
                    const platformUrls: Record<string, string> = {
                      linkedin: `https://www.linkedin.com/feed/?shareActive=true&text=${text}`,
                      twitter: `https://twitter.com/intent/tweet?text=${text}`,
                      facebook: `https://www.facebook.com/sharer/sharer.php?quote=${text}`,
                    };
                    const url = platformUrls[card.platform] || platformUrls.linkedin;
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setPublished(true);
                    fetch(`${API_BASE_URL}/v1/content-queue/${card.contentId}`, {
                      method: 'PATCH',
                      headers: authHeaders(),
                      body: JSON.stringify({ status: 'published' }),
                    }).catch(() => {});
                  }}
                  className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg text-white hover:opacity-90 transition-all"
                  style={{ fontWeight: 500, background: PLATFORM_CONFIG[card.platform]?.color || '#0a66c2' }}
                >
                  <ExternalLink className="w-3 h-3" />
                  Open in {PLATFORM_CONFIG[card.platform]?.label || 'LinkedIn'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
